import IndexStore from 'src/stores';
import type {Dispatch, ReduxStore} from 'src/reducers';
import * as React from 'react';
import logger from 'src/utils/logger';
import {getCurrentLanguageForExternalApps} from 'src/utils/language-support';
import {Provider} from 'react-redux';
import FluxRoot from 'src/flux/root';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import {App as BookingAppV2} from './booking-v2/App';
import {WindowWidthProvider} from 'src/components/lib/responsive';
import I18nContext from 'src/contexts/i18n-context';
import labelI18nInstance, {
  ensureInitialized,
} from 'src/components/lib/i18n/new-i18n';
import {getAgencyConfig} from 'src/action-creators/agency';
import {getReleaseFlags} from 'src/action-creators/product-flags';

import 'src/components/app.global.css';
import styles from 'src/components/app.css';
import {trackBrowserDetails} from './booking-v2/analytics/analytics-events';


const SchedulerApp = ({
  agentId,
  templateSuffix,
  reduxStore,
  senseStore,
  env,
}: {
  agentId: string;
  templateSuffix: string;
  reduxStore: ReduxStore;
  senseStore: IndexStore;
  env: string;
}) => {
  const {id: agencyId, name: agencyName} = reduxStore.getState().agency.agency;
  const [_i18nState, setI18nState] = React.useState(labelI18nInstance);
  const [i18nReady, setI18nReady] = React.useState(false);
  const {i18NEnableLanguageSupport: isLanguageSupportEnabled} =
    reduxStore.getState().agency.config;
  const countyCode =
    reduxStore.getState().agency.config.defaultPhoneCountry || 'US';

  // clean up localStorage if language support is disabled
  if (!isLanguageSupportEnabled) {
    try {
      localStorage.removeItem('selectedLanguage');
    } catch (e: any) {
      logger.error('Error removing selectedLanguage from localStorage', e);
    }
  }
  const resolvedLanguage = getCurrentLanguageForExternalApps(
    isLanguageSupportEnabled,
  );
  React.useEffect(() => {
    trackBrowserDetails(agencyId, agencyName);
    const initializeI18n = async () => {
      try {
        await ensureInitialized(resolvedLanguage); // ensure translations are initialized
        setI18nReady(true);
      } catch (error: any) {
        logger.error('Error initializing translations:', error);
        setI18nReady(true); // allow app to proceed even if translations fail
      }
    };

    initializeI18n();

    const handleLanguageChanged = () => {
      setI18nState({...labelI18nInstance});
    };

    labelI18nInstance.on('languageChanged', handleLanguageChanged);

    return () => {
      labelI18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  if (!i18nReady) {
    return (
      <div className={styles.i18nLoader}>
        <CircularLoader size="large" />
      </div>
    );
  }

  return (
    // @ts-expect-error - TS2786 - 'Provider' cannot be used as a JSX component.
    <Provider store={reduxStore}>
      <FluxRoot store={senseStore}>
        <div className={styles.container}>
          <div className={styles.body}>
            <WindowWidthProvider>
              <I18nContext.Provider value={{labelI18nInstance}}>
                <BookingAppV2
                  currentRoute={agentId}
                  id={templateSuffix}
                  env={env}
                  countyCode={countyCode}
                />
              </I18nContext.Provider>
            </WindowWidthProvider>
          </div>
        </div>
      </FluxRoot>
    </Provider>
  );
};

export default SchedulerApp;

export const schedulerDependencies = ({
  dispatch,
}: {
  dispatch: Dispatch;
}): Promise<Array<unknown>> => {
  return Promise.all([
    dispatch(getAgencyConfig()),
    dispatch(getReleaseFlags()),
  ]);
};
